import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Separator, TextField, Button, Divider, Paragraph, Checkbox, Row, InputGroup } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { v4 as uuid } from 'uuid'
import { i18n } from '@fs/zion-locale'

const addItemButtonCss = css`
  margin-top: -10px;
`
const addSelectAllButtonCss = css`
  margin-bottom: 10px;
`
const creatorLabel = (name) => i18n.t('editCreator.label', 'Select creator {name}', { name })

export default function CheckboxListInputs({
  label,
  disabled,
  inputPlaceHolder,
  values,
  setValues,
  onSelectionChange,
  selectAllLabel = '',
}) {
  const [selected, setSelected] = useState({})
  const [selectAllChecked, setSelectAllChecked] = useState(false)
  const disabledRef = useRef()

  const setAllKeys = (vals, bool) => {
    const newSet = {}
    if (Object.keys(vals || {}).length > 0) {
      Object.keys(vals).forEach((key) => {
        newSet[key] = bool
      })
    }
    setSelectAllChecked(bool)
    setSelected(newSet)
  }

  const resetSelected = useCallback(() => {
    setAllKeys(values, false)
  }, [values])

  const selectAll = useCallback(() => {
    setAllKeys(values, true)
  }, [values])

  useEffect(() => {
    resetSelected()
  }, [resetSelected])

  useEffect(() => {
    if (Object.keys(selected).length > 0) onSelectionChange(selected)
  }, [onSelectionChange, selected])

  // so the checkboxes reset if cancel is clicked
  useEffect(() => {
    if (disabledRef.current && !disabled) resetSelected()
    if (disabled !== disabledRef) disabledRef.current = disabled
  }, [disabled, resetSelected])

  const getAllSelected = useCallback((obj) => {
    const mix = new Set()
    Object.keys(obj).forEach((key) => {
      mix.add(obj[key])
    })
    return mix.size === 1 ? [...mix][0] : undefined
  }, [])

  const handleSelectAll = () => {
    if (getAllSelected(selected)) {
      resetSelected()
    } else {
      selectAll()
    }
  }

  const handleValueChange = (target, key) => {
    setValues((prevValues) => {
      const newValues = { ...prevValues }
      newValues[key] = target.value
      return newValues
    })
  }

  const handleSelectionChange = (target, key) => {
    setSelected((prevState) => {
      const newState = { ...prevState }
      newState[key] = target.checked
      setSelectAllChecked(getAllSelected(newState))
      return newState
    })
  }

  const handleNewItemClick = () => {
    setValues((prevValues) => {
      const newId = uuid()
      const newValues = { ...prevValues }
      newValues[newId] = ''
      return newValues
    })
  }

  return (
    <>
      <Separator size="nano" />
      <Paragraph size="xs">{label}</Paragraph>
      {selectAllLabel && (
        <div css={addSelectAllButtonCss}>
          <Checkbox
            checked={selectAllChecked}
            mixedState={selectAllChecked === undefined}
            onChange={handleSelectAll}
            name={selectAllLabel}
            label={selectAllLabel}
          />
          <Divider marginY="nano" />
        </div>
      )}
      {Object.keys(values || {}).length > 0 &&
        Object.keys(values).map((key) => (
          <InputGroup key={key}>
            <Row gutters="xs">
              <Checkbox
                aria-label={creatorLabel(values[key])}
                checked={selected[key]}
                onChange={({ currentTarget }) => handleSelectionChange(currentTarget, key)}
                name="input_checkbox"
              />
              <TextField
                value={values[key]}
                onChange={({ currentTarget }) => handleValueChange(currentTarget, key)}
                placeholder={inputPlaceHolder}
                type="text"
                disabled={disabled}
                growX
              />
            </Row>
          </InputGroup>
        ))}
      <div css={!!values?.length && addItemButtonCss}>
        <Button
          Icon={ContentAdd}
          onClick={handleNewItemClick}
          disabled={disabled}
          data-testid="checkbox-list-inputs-button"
        >
          {label}
        </Button>
      </div>
      <Separator size="xxs" />
      <Divider />
    </>
  )
}
